/**
 * Cross-source activity browser (lexide + lexruntime + harn).
 *
 * Layout (mirrors FeedbackBrowser):
 *   header → ActivitySummary (StatCards + AreaChart) → ActivityFilters →
 *   master/detail (events table + ActivityDetailPane).
 *
 * Selection is URL-bound: /activity/:eventId is the deep link.
 * Filter changes are URL-quiet — the URL stays put when the
 * selected event falls outside the new filter; the detail pane
 * then shows "not in current view".
 *
 * Pagination: cursor-based via the runtime's `activity.list` RPC.
 * "Load more" appends; any filter change resets the cursor.
 *
 * Drill-down: clicking a "View ..." button in the detail pane
 * patches the filter state via `applyFilterPatch`.
 */

import { Download, Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { ActivityEvent } from "@lexiun/lx-obs";

import { ActivityDetailPane } from "@/components/activity/ActivityDetailPane";
import {
  ActivityFilters,
  EMPTY_FILTER,
  type ActivityFilterState,
} from "@/components/activity/ActivityFilters";
import { ActivitySummary } from "@/components/activity/ActivitySummary";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import {
  ActivityRpcClient,
  type ActivityGetResult,
  type ActivityListParams,
  type ActivitySummaryResult,
} from "@/lib/activity-rpc";

const PAGE_SIZE = 50;

export function ActivityBrowser() {
  const { rpc, state } = useGatewayRpc();
  const navigate = useNavigate();
  const { eventId: urlEventId } = useParams<{ eventId?: string }>();

  const client = useMemo(() => new ActivityRpcClient(rpc), [rpc]);

  const [filter, setFilter] = useState<ActivityFilterState>(EMPTY_FILTER);
  const [items, setItems] = useState<ActivityEvent[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<ActivitySummaryResult | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [detail, setDetail] = useState<ActivityGetResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const selectedId = urlEventId ?? null;
  const setSelectedId = useCallback(
    (id: string | null) => {
      navigate(id ? `/activity/${id}` : "/activity");
    },
    [navigate],
  );

  const queryParams = useMemo<ActivityListParams>(
    () => buildQueryParams(filter),
    [filter],
  );

  const load = useCallback(
    async (append: boolean) => {
      if (state !== "connected") return;
      try {
        if (!append) setLoading(true);
        const res = await client.list({
          ...queryParams,
          cursor: append ? cursor : undefined,
          limit: PAGE_SIZE,
        });
        setItems((prev) => (append ? [...prev, ...res.events] : res.events));
        setCursor(res.next_cursor);
        setHasMore(Boolean(res.next_cursor));
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load activity");
      } finally {
        setLoading(false);
      }
    },
    [client, queryParams, cursor, state],
  );

  // Refetch from the start on filter change (cursor reset).
  useEffect(() => {
    setCursor(undefined);
    void load(false);
    // queryParams covers all filter inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, state]);

  // Summary tracks the same time window as the list (date-only —
  // free-text and id filters don't affect the summary so admins
  // see global health while drilling).
  useEffect(() => {
    if (state !== "connected") return;
    const since = isoToEpochStartUtc(filter.fromDate);
    const until = isoToEpochEndUtc(filter.toDate);
    setSummaryLoading(true);
    client
      .summary({ since, until, bucket: pickBucket(since, until) })
      .then((r) => setSummary(r))
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [client, filter.fromDate, filter.toDate, state]);

  // Fetch detail (event + ±60 s context strip) when selection
  // changes. Falls back to whatever's in the table if the RPC
  // fails (event still selectable, context strip empty).
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    client
      .get(selectedId)
      .then((r) => setDetail(r))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [client, selectedId]);

  const onDrillDown = useCallback(
    (patch: Partial<ActivityFilterState>) => {
      setFilter((prev) => {
        const next = { ...prev, ...patch };
        // A type-specific drill (e.g. "View matching agent.run_end")
        // must clear conflicting selectors, otherwise an existing
        // category/errorsOnly filter will hide the very event we're
        // navigating to. Same for category drills overriding type.
        if (patch.type !== undefined) {
          next.category = "";
          next.errorsOnly = false;
        }
        if (patch.category !== undefined) {
          next.errorsOnly = false;
        }
        return next;
      });
    },
    [],
  );

  const onExportCsv = useCallback(() => {
    if (items.length === 0) return;
    const csv = toCsv(items);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [items]);

  const detailEvent = detail?.event ?? items.find((e) => e.id === selectedId) ?? null;
  const notInView = Boolean(selectedId) && !detailLoading && !detailEvent;

  if (state !== "connected" && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Activity</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to runtime…" : "Runtime disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-indigo-500" />
        <h1 className="text-xl font-semibold">Activity</h1>
        <span className="text-sm text-slate-400 dark:text-slate-500">
          User events across LexIDE, plugin hooks, and harnesses
        </span>
        <button
          type="button"
          onClick={onExportCsv}
          disabled={items.length === 0}
          className="ml-auto flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
          title="Download the current page as CSV"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </header>

      <ActivitySummary summary={summary} loading={summaryLoading} />
      <ActivityFilters value={filter} onChange={setFilter} />

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        <ActivityList
          items={items}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={() => load(true)}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        <ActivityDetailPane
          selectedId={selectedId}
          event={detailEvent}
          context={detail?.context ?? []}
          loading={detailLoading}
          notInView={notInView}
          onDrillDown={onDrillDown}
          onSelect={setSelectedId}
        />
      </div>
    </div>
  );
}

interface ListProps {
  items: ActivityEvent[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function ActivityList({ items, loading, hasMore, onLoadMore, selectedId, onSelect }: ListProps) {
  return (
    <div className="flex min-h-0 flex-col rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <span className="text-sm font-semibold">
          {items.length} event{items.length === 1 ? "" : "s"}
        </span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-400">
            No events match these filters.
          </div>
        )}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => onSelect(e.id)}
                className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                  selectedId === e.id
                    ? "bg-indigo-50 dark:bg-indigo-950"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      e.category === "error"
                        ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                    }`}
                  >
                    {e.category}
                  </span>
                  <span className="font-mono text-xs">{e.type}</span>
                  <span className="ml-auto text-xs text-slate-400">
                    {new Date(e.ts).toLocaleTimeString()}
                  </span>
                </div>
                <div className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] dark:bg-slate-800">
                    {e.source}/{e.subsystem}
                  </span>
                  {e.route && <span className="ml-2 font-mono">{e.route}</span>}
                  {e.task_id && (
                    <span className="ml-2 font-mono text-indigo-600 dark:text-indigo-400">
                      task:{e.task_id.slice(0, 8)}
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {hasMore && (
        <div className="border-t border-slate-200 p-2 text-center dark:border-slate-700">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="rounded-md border border-slate-200 px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

// ── helpers (exported for tests) ──────────────────────────────────────────

export function buildQueryParams(filter: ActivityFilterState): ActivityListParams {
  const since = filter.fromDate ? isoToEpochStartUtc(filter.fromDate) : undefined;
  const until = filter.toDate ? isoToEpochEndUtc(filter.toDate) : undefined;
  return {
    since,
    until,
    user_id: orUndef(filter.user_id),
    session_id: orUndef(filter.session_id),
    case_id: orUndef(filter.case_id),
    agent_id: orUndef(filter.agent_id),
    task_id: orUndef(filter.task_id),
    source: filter.source || undefined,
    subsystem: orUndef(filter.subsystem),
    category: filter.errorsOnly ? "error" : filter.category || undefined,
    type: orUndef(filter.type),
  };
}

function orUndef(s: string): string | undefined {
  const t = s.trim();
  return t ? t : undefined;
}

export function isoToEpochStartUtc(iso: string): number {
  // YYYY-MM-DD → UTC midnight ms
  return new Date(`${iso}T00:00:00.000Z`).getTime();
}
export function isoToEpochEndUtc(iso: string): number {
  return new Date(`${iso}T23:59:59.999Z`).getTime();
}

export function pickBucket(since: number, until: number): "hour" | "day" {
  // Window > 3 days → day buckets; otherwise hour buckets.
  return until - since > 3 * 24 * 60 * 60 * 1000 ? "day" : "hour";
}

export function toCsv(events: ActivityEvent[]): string {
  const cols = [
    "id",
    "ts",
    "user_id",
    "session_id",
    "case_id",
    "task_id",
    "source",
    "subsystem",
    "category",
    "type",
    "route",
  ] as const;
  const header = cols.join(",");
  const rows = events.map((e) =>
    cols
      .map((c) => csvCell((e as Record<string, unknown>)[c]))
      .join(","),
  );
  return [header, ...rows].join("\n");
}

function csvCell(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
